import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Link, FileSearch, User, ScanEye, Loader2, X, Image, Video, Music, Share2, MessageSquare, Mic, MicOff, ChevronDown, ChevronUp, Camera } from 'lucide-react';
import axios from 'axios';
import LLMSelector from './LLMSelector';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AnalysisInput({ onAnalyze, loading, user }) {
  const { t } = useLanguage();
  const [inputType, setInputType] = useState('url');
  const [inputValue, setInputValue] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [socialScreenshot, setSocialScreenshot] = useState(null);
  const [socialScreenshotPreview, setSocialScreenshotPreview] = useState(null);
  const [selectedLlms, setSelectedLlms] = useState([]);
  const [availableLlms, setAvailableLlms] = useState({});
  const [customInstructions, setCustomInstructions] = useState('');
  const [showInstructions, setShowInstructions] = useState(true);
  const [isListening, setIsListening] = useState(false);
  const [micTarget, setMicTarget] = useState(null); // 'input' or 'instructions'

  useEffect(() => {
    axios.get(`${API_URL}/api/llms`).then(res => {
      setAvailableLlms(res.data);
      const firstAvailable = Object.entries(res.data).find(([, info]) => info.available);
      if (firstAvailable) {
        setSelectedLlms([firstAvailable[0]]);
      }
    }).catch(() => {});
  }, [inputType]);

  const startListening = useCallback((target) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (target === 'instructions') {
        setCustomInstructions(prev => prev ? prev + ' ' + transcript : transcript);
      } else {
        setInputValue(prev => prev ? prev + ' ' + transcript : transcript);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setMicTarget(null);
    };

    recognition.onerror = () => {
      setIsListening(false);
      setMicTarget(null);
    };

    setIsListening(true);
    setMicTarget(target);
    recognition.start();
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      if (inputType !== 'photo') {
        setInputType('file');
      }
    }
  }, [inputType]);

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps, isDragActive: isPhotoDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'] },
    maxSize: 10 * 1024 * 1024,
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.webm', '.mov'],
      'audio/*': ['.mp3', '.wav', '.ogg']
    },
    maxSize: 10 * 1024 * 1024
  });

  const handleSocialScreenshot = (file) => {
    setSocialScreenshot(file);
    const reader = new FileReader();
    reader.onload = (e) => setSocialScreenshotPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = () => {
    if (selectedLlms.length === 0) return;
    const instructions = customInstructions.trim() || null;
    if (inputType === 'photo') {
      if (uploadedFile) {
        onAnalyze('file', uploadedFile, selectedLlms, null, instructions);
      }
      return;
    }
    if (inputType === 'social_post') {
      if (inputValue.trim()) {
        onAnalyze('social_post', inputValue, selectedLlms, socialScreenshot, instructions);
      }
      return;
    }
    if (uploadedFile) {
      onAnalyze('file', uploadedFile, selectedLlms, null, instructions);
    } else if (inputValue.trim()) {
      onAnalyze(inputType, inputValue, selectedLlms, null, instructions);
    }
  };

  const inputTypes = [
    { id: 'url', label: t('input_url'), icon: Link },
    { id: 'text', label: t('input_text'), icon: FileSearch },
    { id: 'photo', label: t('input_photo'), icon: Camera },
    { id: 'social_post', label: t('input_social'), icon: Share2 },
    { id: 'profile', label: t('input_profile'), icon: User }
  ];

  const creditCost = selectedLlms.length + (uploadedFile || inputType === 'photo' ? 5 : 0);

  return (
    <div className="w-full max-w-3xl mx-auto">
      {user && (
        <LLMSelector
          selectedLlms={selectedLlms}
          setSelectedLlms={setSelectedLlms}
          maxLlms={user.max_llms || 1}
          availableLlms={availableLlms}
        />
      )}

      <div className="flex items-center justify-center gap-2 mb-6">
        {inputTypes.map(type => (
          <button
            key={type.id}
            onClick={() => { setInputType(type.id); setUploadedFile(null); setSocialScreenshot(null); setSocialScreenshotPreview(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
              inputType === type.id
                ? 'bg-gradient-to-r from-emerald-500/20 to-blue-500/20 border border-emerald-500/30 text-white'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <type.icon className="w-4 h-4" />
            <span className="text-sm font-medium">{type.label}</span>
          </button>
        ))}
      </div>

      {inputType === 'photo' ? (
        <div {...getPhotoRootProps()} className={`rounded-2xl bg-slate-900/80 border-2 border-dashed transition-all input-glow ${isPhotoDragActive ? 'border-emerald-500 bg-emerald-500/10' : 'border-white/10'}`}>
          <input {...getPhotoInputProps()} />
          {uploadedFile ? (
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl overflow-hidden border border-white/10">
                  <img src={URL.createObjectURL(uploadedFile)} alt="Preview" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-white font-medium">{uploadedFile.name}</p>
                  <p className="text-sm text-slate-400">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  <p className="text-xs text-emerald-400 mt-1">{t('input_photo_cost')}</p>
                </div>
              </div>
              <button onClick={() => setUploadedFile(null)} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-12 cursor-pointer">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-blue-500/20 flex items-center justify-center">
                <Camera className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-white font-medium">{t('input_photo_drop')}</p>
              <p className="text-slate-500 text-xs">{t('input_photo_formats')}</p>
              <p className="text-emerald-400 text-sm font-medium mt-1">{t('input_photo_cost')}</p>
            </div>
          )}

        </div>
      ) : inputType === 'social_post' ? (
        <div className="rounded-2xl bg-slate-900/80 border-2 border-dashed border-white/10 transition-all input-glow">
          <div className="p-6">
            <label className="block text-sm font-medium text-emerald-400 mb-2">{t('input_social_text_label')}</label>
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('input_social_text_ph')}
              className="w-full bg-transparent text-white text-lg placeholder:text-slate-500 resize-none focus:outline-none min-h-[80px]"
              rows={3}
            />
          </div>
          <div className="px-6 pb-6 pt-2 border-t border-white/5">
            <label className="block text-sm font-medium text-blue-400 mb-2">{t('input_social_img_label')}</label>
            {socialScreenshotPreview ? (
              <div className="flex items-center gap-4">
                <img src={socialScreenshotPreview} alt="Screenshot" className="h-20 rounded-lg border border-white/10 object-cover" />
                <div className="flex-1">
                  <p className="text-white text-sm font-medium">{socialScreenshot.name}</p>
                  <p className="text-slate-400 text-xs">{(socialScreenshot.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <button
                  onClick={() => { setSocialScreenshot(null); setSocialScreenshotPreview(null); }}
                  className="p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-blue-500/30 hover:bg-blue-500/5 transition-all">
                <Image className="w-8 h-8 text-blue-400" />
                <span className="text-sm text-slate-400">{t('input_social_img_ph')}</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => { if (e.target.files[0]) handleSocialScreenshot(e.target.files[0]); }}
                />
              </label>
            )}
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`relative rounded-2xl bg-slate-900/80 border-2 border-dashed transition-all input-glow ${
            isDragActive
              ? 'border-emerald-500 bg-emerald-500/10'
              : 'border-white/10 hover:border-white/20'
          }`}
        >
          <input {...getInputProps()} />

          {uploadedFile ? (
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {uploadedFile.type.startsWith('image') ? (
                  <Image className="w-10 h-10 text-emerald-400" />
                ) : uploadedFile.type.startsWith('video') ? (
                  <Video className="w-10 h-10 text-blue-400" />
                ) : (
                  <Music className="w-10 h-10 text-purple-400" />
                )}
                <div>
                  <p className="text-white font-medium">{uploadedFile.name}</p>
                  <p className="text-sm text-slate-400">{(uploadedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setUploadedFile(null); }}
                className="p-2 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          ) : (
            <div className="p-6">
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder={
                  inputType === 'url' ? t('input_ph_url') :
                  inputType === 'profile' ? t('input_ph_profile') :
                  t('input_ph_text')
                }
                className="w-full bg-transparent text-white text-lg placeholder:text-slate-500 resize-none focus:outline-none min-h-[60px]"
                rows={2}
              />
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-center gap-2 text-slate-500">
                <Upload className="w-4 h-4" />
                <span className="text-sm">{t('input_dropzone')}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Instructions personnalisees (gratuit) */}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => setShowInstructions(!showInstructions)}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          <span>{t('instructions_label')}</span>
          {showInstructions ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {showInstructions && (
          <div className="mt-2 relative">
            <textarea
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder={t('instructions_ph')}
              className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 pr-12 text-white text-sm placeholder:text-slate-500 resize-none focus:outline-none focus:border-emerald-500/30"
              rows={2}
              maxLength={500}
            />
            <button
              type="button"
              onClick={() => startListening('instructions')}
              className={`absolute right-3 top-3 p-1.5 rounded-lg transition-colors ${
                isListening && micTarget === 'instructions'
                  ? 'bg-rose-500/20 text-rose-400'
                  : 'text-slate-500 hover:text-white hover:bg-white/5'
              }`}
              title={isListening && micTarget === 'instructions' ? t('mic_listening') : t('mic_start')}
            >
              {isListening && micTarget === 'instructions' ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            {isListening && micTarget === 'instructions' && (
              <p className="text-xs text-rose-400 mt-1 animate-pulse">{t('mic_listening')}</p>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-3 mt-6">
        <button
          onClick={handleSubmit}
          disabled={loading || (!inputValue.trim() && !uploadedFile && inputType !== 'photo') || selectedLlms.length === 0 || (user && user.credits < creditCost) || (inputType === 'social_post' && !inputValue.trim())}
          className="px-8 py-4 rounded-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white font-semibold text-lg shadow-lg shadow-emerald-500/20 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('input_analyzing')}
            </>
          ) : (
            <>
              <ScanEye className="w-5 h-5" />
              {t('input_analyze')} ({creditCost} {creditCost > 1 ? t('credits') : t('credit')})
            </>
          )}
        </button>

        {user && user.credits < creditCost && (
          <p className="text-rose-400 text-sm">
            {t('input_credits_low', { n: user.credits })}
          </p>
        )}
      </div>
    </div>
  );
}
